import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Select, Spin, message, DatePicker } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DollarOutlined, UserOutlined, TeamOutlined, ShopOutlined, RiseOutlined, AlertOutlined } from '@ant-design/icons';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { dashboardService } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const DashboardExecutivo = () => {
  const [loading, setLoading] = useState(true);
  const [dadosExecutivo, setDadosExecutivo] = useState(null);
  const [dadosFinanceiro, setDadosFinanceiro] = useState(null);
  const [receitaPorModalidade, setReceitaPorModalidade] = useState([]);
  const [topEscolas, setTopEscolas] = useState([]);
  const [mesAno, setMesAno] = useState(dayjs());

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    carregarDados();
    // Auto-refresh a cada 5 minutos
    const interval = setInterval(carregarDados, 300000);
    return () => clearInterval(interval);
  }, [mesAno]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {
        mes: mesAno.month() + 1,
        ano: mesAno.year()
      };

      const [executivo, financeiro, modalidades, escolas] = await Promise.all([
        dashboardService.getExecutivo(params),
        dashboardService.getFinanceiro(params),
        dashboardService.getReceitaPorModalidade(params),
        dashboardService.getTopEscolas(params)
      ]);

      setDadosExecutivo(executivo.data);
      setDadosFinanceiro(financeiro.data);
      setReceitaPorModalidade(modalidades.data.data || []);
      setTopEscolas(escolas.data.data || []);
    } catch (error) {
      message.error('Erro ao carregar dados do dashboard');
      console.error(error);
    }
    setLoading(false);
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  if (loading || !dadosExecutivo || !dadosFinanceiro) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Carregando dashboard..." />
      </div>
    );
  }

  const { kpis } = dadosExecutivo;
  const { fluxo_caixa_atual, historico_12_meses } = dadosFinanceiro;

  return (
    <div className="dashboard-page" style={{ padding: '24px' }}>
      {/* Cabeçalho */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard Executivo</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Visão geral do negócio</p>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={mesAno}
            onChange={(date) => setMesAno(date || dayjs())}
            format="MMMM/YYYY"
            style={{ width: 200 }}
            // Fase 3: este dashboard mostra realizado/historico, nao forecast --
            // forecast fica nos indicadores proprios do Executivo V2. Bloqueia
            // qualquer mes POSTERIOR ao atual (o mes atual inteiro continua
            // disponivel); meses passados nunca sao bloqueados.
            disabledDate={(current) => current && current > dayjs().endOf('month')}
          />
        </Col>
      </Row>

      {/* KPIs Principais */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Receita Mensal"
              value={kpis.receita_mensal.realizada}
              precision={2}
              prefix={<DollarOutlined />}
              suffix={
                kpis.receita_mensal.crescimento === null ? (
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>—</span>
                ) : (
                  <span style={{ fontSize: '14px', color: kpis.receita_mensal.crescimento >= 0 ? '#3f8600' : '#cf1322' }}>
                    {kpis.receita_mensal.crescimento >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {Math.abs(kpis.receita_mensal.crescimento).toFixed(1)}%
                  </span>
                )
              }
              valueStyle={{ color: '#3f8600' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Prevista: {formatarMoeda(kpis.receita_mensal.prevista)} | Total: {formatarMoeda(kpis.receita_mensal.total)}
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Alunos Ativos"
              value={kpis.alunos_ativos.total}
              prefix={<UserOutlined />}
              suffix={
                kpis.alunos_ativos.crescimento === null ? (
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>—</span>
                ) : (
                  <span style={{ fontSize: '14px', color: kpis.alunos_ativos.crescimento >= 0 ? '#3f8600' : '#cf1322' }}>
                    {kpis.alunos_ativos.crescimento >= 0 ? '+' : ''}{kpis.alunos_ativos.crescimento}
                  </span>
                )
              }
              valueStyle={{ color: '#1890ff' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              vs mês anterior
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Matrículas Ativas"
              value={kpis.matriculas_ativas?.total || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Total de matrículas ativas no sistema
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Professores Ativos"
              value={kpis.professores_ativos}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Equipe ativa
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Inadimplência Atual"
              value={kpis.inadimplencia.inadimplencia_valor_total}
              precision={2}
              prefix={<AlertOutlined />}
              formatter={(value) => formatarMoeda(value)}
              valueStyle={{ color: '#cf1322' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {kpis.inadimplencia.quantidade === 1
                ? '1 título vencido'
                : `${kpis.inadimplencia.quantidade} títulos vencidos`}
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Escolas Ativas"
              value={kpis.escolas_ativas}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Unidades em operação
            </p>
          </Card>
        </Col>
      </Row>

      {/* Fluxo de Caixa */}
      <Card title="Fluxo de Caixa" style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <div style={{ background: '#f0f5ff', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 12px', color: '#1890ff' }}>📥 Entradas</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#52c41a' }}>
                {formatarMoeda(fluxo_caixa_atual.entradas)}
              </div>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ background: '#fff1f0', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 12px', color: '#ff4d4f' }}>📤 Saídas</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff4d4f' }}>
                {formatarMoeda(fluxo_caixa_atual.saidas)}
              </div>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ background: fluxo_caixa_atual.saldo >= 0 ? '#f6ffed' : '#fff1f0', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 12px', color: fluxo_caixa_atual.saldo >= 0 ? '#52c41a' : '#ff4d4f' }}>
                💵 Saldo
              </h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: fluxo_caixa_atual.saldo >= 0 ? '#52c41a' : '#ff4d4f' }}>
                {formatarMoeda(fluxo_caixa_atual.saldo)}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Resultado Operacional */}
      <Card title="Resultado Operacional" style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12}>
            <div style={{ background: fluxo_caixa_atual.resultado_operacional >= 0 ? '#f6ffed' : '#fff1f0', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 12px', color: fluxo_caixa_atual.resultado_operacional >= 0 ? '#52c41a' : '#ff4d4f' }}>
                📊 Resultado Operacional
              </h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: fluxo_caixa_atual.resultado_operacional >= 0 ? '#52c41a' : '#ff4d4f' }}>
                {formatarMoeda(fluxo_caixa_atual.resultado_operacional)}
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div style={{ background: '#f0f5ff', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 12px', color: '#1890ff' }}>
                📈 Margem Operacional
              </h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>
                {fluxo_caixa_atual.margem_operacional === null ? '—' : `${fluxo_caixa_atual.margem_operacional.toFixed(1)}%`}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Gráficos */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Receita vs Despesas (12 meses)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={historico_12_meses}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip formatter={(value) => formatarMoeda(value)} />
                <Legend />
                <Line type="monotone" dataKey="receitas" stroke="#52c41a" strokeWidth={2} name="Receitas" />
                <Line type="monotone" dataKey="despesas" stroke="#ff4d4f" strokeWidth={2} name="Despesas" />
                <Line type="monotone" dataKey="saldo" stroke="#1890ff" strokeWidth={2} name="Saldo" strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Receita por Modalidade">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={receitaPorModalidade}
                  dataKey="valor"
                  nameKey="modalidade"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.modalidade}`}
                >
                  {receitaPorModalidade.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatarMoeda(value)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Top 5 Escolas por Número de Alunos">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topEscolas} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="escola" type="category" width={150} />
                <Tooltip />
                <Bar dataKey="alunos" fill="#1890ff" name="Alunos" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardExecutivo;
